import React, {useEffect, useState} from 'react';
import {MenuItem} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {ItemRenderer, MultiSelect2} from '@blueprintjs/select';

import {IItem} from './types';
import {itemPredicate} from './utils';

interface PopoverProps {
  usePortal: boolean;
  portalClassName?: string;
}

interface TagInputProps {
  placeholder: string;
  rightElement: any;
}

interface SimpleMultiSelectProps {
  selected: IItem[];
  disabled?: boolean;
  onChange?: (value: IItem[]) => void;
  options: IItem[];
  onSelect?: (group: IItem) => void;
  onRemove?: (group: IItem) => void;
  popoverProps?: PopoverProps;
  tagInputProps?: TagInputProps;
}

const SelectElement = MultiSelect2.ofType<IItem>();

const PopoverDefaultProps: PopoverProps = {
  usePortal: false,
  portalClassName: '',
}

const TagInputDefaultProps: TagInputProps = {
  placeholder: '',
  rightElement: undefined
}


const renderItem: ItemRenderer<IItem> = (item: IItem, {handleClick, modifiers, query}: any) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }

  return (
    <MenuItem
      text={item.label}
      key={item.value}
      onClick={handleClick}/>
  )
};

const tagRenderer = (item: IItem) => item.value;

export const SimpleMultiSelect = ({
  selected,
  disabled = true,
  onChange,
  options,
  onSelect,
  onRemove,
  popoverProps = PopoverDefaultProps,
  tagInputProps = TagInputDefaultProps,
}: SimpleMultiSelectProps) => {
  const [selectedItems, setSelectedItems] = useState<IItem[]>(selected);
  const [filteredItems, setFilteredItems] = useState<IItem[]>([]); // items that can be selected

  useEffect(() => {
    /*
     * `selectedItems` are used by reference;
     * `filteredItems` equal `options` without `selectedItems`
     */
    const values = selectedItems.map((s: IItem) => s.value);
    setFilteredItems(options.filter((o: IItem) => !values.includes(o.value)));
  }, [options?.length]);

  useEffect(() => {
    /*
     * If selected items change
     * - modify filteredItems
     * - trigger `onChange`
     */
    const values = selectedItems.map((s: IItem) => s.value);

    /*
     * `filteredItems` equal `options` without `selectedItems`
     *
     */
    setFilteredItems(options.filter((o: IItem) => !values.includes(o.value)));
    /*
     * on items length change
     */
    onChange && onChange(selectedItems);
  }, [selectedItems.length]);

  const onItemSelect = (item: IItem) => {
    setSelectedItems((oldItems: IItem[]) => [...oldItems, item]);
    onSelect && onSelect(item);
  };

  const onItemRemove = (item: IItem) => {
    setSelectedItems(
      (oldItems: IItem[]) =>
        [
          ...oldItems.filter((oldItem: IItem) => oldItem.value !== item.value)
        ]
    );
    onRemove && onRemove(item);
  };

  return (
    <SelectElement
      fill={true}
      items={filteredItems}
      itemPredicate={itemPredicate}
      selectedItems={selectedItems}
      itemRenderer={renderItem}
      onItemSelect={onItemSelect}
      onRemove={onItemRemove}
      resetOnSelect={true}
      tagRenderer={tagRenderer}
      popoverProps={{
        captureDismiss: true /* close only this popover */,
        usePortal: popoverProps?.usePortal || false,
        minimal: true,
        portalClassName: popoverProps?.portalClassName || 'simple-multi-select-portal',
      }}
      tagInputProps={{
        placeholder: tagInputProps.placeholder || '',
        disabled: disabled,
        rightElement: tagInputProps?.rightElement || undefined,
      }}
    />
  )
};
